/**
 * Wiederverwendbare Sub-Prozesse („Bausteine").
 *
 * Ein Baustein ist ein zusätzlicher `bpmn:Process` in denselben
 * `bpmn:definitions`, mit eigenem `bpmndi:BPMNDiagram`. Verwendet wird er auf
 * zwei Arten, und die Wahl trifft der Nutzer je Einfügung:
 *
 *   - **als Verweis** — `bpmn:CallActivity` mit `calledElement` auf die
 *     Prozess-ID. Eine Änderung am Baustein wirkt an jeder Aufrufstelle.
 *   - **als Kopie** — ein gewöhnlicher `bpmn:SubProcess` mit kopierten
 *     Elementen. Danach unabhängig, wie bisher.
 *
 * Alles bleibt in einer Datei; `calledElement` zeigt nie nach außen. BPMN
 * kennt für dateiübergreifende Auflösung keinen Standard, und die Datei soll
 * für sich lauffähig bleiben.
 *
 * Gemessen an bpmn-js 17: Der Importer lädt nur das erste Diagramm samt
 * seiner Unter-Ebenen, ein Baustein ist also zunächst unsichtbar. Er
 * überlebt aber Laden und Speichern unverändert — Prozess, Diagramm, Ebene
 * mit vollständiger Anordnung und die Verweise. Sichtbar wird er über
 * `modeler.open(<Diagramm-ID>)`, siehe openBaustein im BpmnEditor.
 */

/** Attribut, das einen Prozess als Baustein ausweist. */
const MARKER = 'custom:baustein';

/** Ereignis, mit dem ein Provider die Anzeige einer Ebene anfordert. */
export const OPEN_EVENT = 'baustein.open';

/**
 * Ereignis, mit dem ein Provider eine Auswahl anfordert.
 *
 * Frage und Antwort ueber den Ereignisbus: Der Provider auf der
 * Zeichenflaeche kennt React nicht, die Dialoge leben aber dort. Er legt eine
 * `resolve`-Funktion in die Nachricht und wartet; die React-Seite zeigt den
 * Dialog und ruft sie mit der Wahl auf. Ein Zuhoerer, der die Anfrage
 * annimmt, gibt `true` zurueck — sonst faellt der Provider auf eine
 * Nummerneingabe zurueck, statt ewig zu warten.
 */
export const SELECT_EVENT = 'baustein.select';

// ─── Lesen ─────────────────────────────────────────────────────────

function attr(bo, name) {
  if (!bo) return undefined;
  try {
    const v = bo.get(name);
    if (v !== undefined) return v;
  } catch { /* moddle kennt die Eigenschaft nicht */ }
  return bo.$attrs?.[name];
}

/** Ist dieser Prozess als Baustein ausgewiesen? */
export function isBaustein(process) {
  const v = attr(process, MARKER);
  return v === true || v === 'true';
}

/** Die definitions eines beliebigen Business-Objects. */
export function getDefinitions(bo) {
  let cur = bo;
  while (cur && cur.$type !== 'bpmn:Definitions') cur = cur.$parent;
  return cur;
}

/**
 * Alle Bausteine der Datei, jeweils mit ihrem Diagramm.
 * @returns {{process: object, diagram: object|null, id: string, name: string}[]}
 */
export function listBausteine(definitions) {
  if (!definitions) return [];
  const diagrams = definitions.diagrams || [];
  return (definitions.rootElements || [])
    .filter((el) => el.$type === 'bpmn:Process' && isBaustein(el))
    .map((process) => ({
      process,
      diagram: diagrams.find((d) => d.plane?.bpmnElement === process) || null,
      id: process.id,
      name: process.name || process.id,
    }));
}

/** Der Baustein hinter einer Call Activity, oder null. */
export function bausteinOf(callActivity, definitions) {
  const ziel = attr(callActivity, 'calledElement');
  if (!ziel) return null;
  return listBausteine(definitions).find((b) => b.id === ziel) || null;
}

/**
 * Alle Aufrufstellen eines Bausteins — auch die in anderen Bausteinen und in
 * eingebetteten Sub-Prozessen, darum rekursiv über den ganzen Baum.
 */
export function findCallSites(definitions, processId) {
  const treffer = [];
  const besuche = (container) => {
    for (const el of container.flowElements || []) {
      if (el.$type === 'bpmn:CallActivity' && attr(el, 'calledElement') === processId) {
        treffer.push(el);
      }
      if (el.flowElements) besuche(el);
    }
  };
  for (const root of definitions.rootElements || []) {
    if (root.flowElements) besuche(root);
  }
  return treffer;
}

/** Ein Flow-Element irgendwo im Baum, über seine ID. */
export function findFlowElement(definitions, id) {
  const suche = (container) => {
    for (const el of container.flowElements || []) {
      if (el.id === id) return el;
      const tiefer = el.flowElements ? suche(el) : null;
      if (tiefer) return tiefer;
    }
    return null;
  };
  for (const root of definitions.rootElements || []) {
    const treffer = root.flowElements ? suche(root) : null;
    if (treffer) return treffer;
  }
  return null;
}

/** Die BPMNPlane zu einem Element, oder null. */
export function findPlane(definitions, bpmnElementId) {
  return (definitions.diagrams || [])
    .map((d) => d.plane)
    .find((pl) => pl?.bpmnElement?.id === bpmnElementId) || null;
}

/**
 * Jede ID, die in dieser Datei vergeben ist — Elemente, Prozesse, Diagramme,
 * Ebenen und deren DI.
 *
 * Wichtig fuer das Kopieren: Ein Baustein steht nicht auf der Zeichenflaeche,
 * seine Elemente fehlen also in der Elementregistrierung. Wer nur dort nach
 * Kollisionen sucht, kopiert IDs doppelt in die Datei.
 */
export function alleIds(definitions) {
  const belegt = new Set();
  if (!definitions) return belegt;
  const sammleElement = (o) => {
    if (!o || typeof o !== 'object') return;
    if (o.id) belegt.add(o.id);
    for (const el of o.flowElements || []) sammleElement(el);
    for (const el of o.artifacts || []) sammleElement(el);
  };
  for (const r of definitions.rootElements || []) sammleElement(r);
  for (const d of definitions.diagrams || []) {
    if (d.id) belegt.add(d.id);
    if (d.plane?.id) belegt.add(d.plane.id);
    for (const pe of d.plane?.planeElement || []) {
      if (pe.id) belegt.add(pe.id);
    }
  }
  return belegt;
}

/** Eine ID, die in dieser Datei noch nicht vergeben ist. */
export function freieId(definitions, praefix) {
  const belegt = alleIds(definitions);
  let n = 1;
  while (belegt.has(`${praefix}_${n}`)) n++;
  return `${praefix}_${n}`;
}

// ─── Schreiben ─────────────────────────────────────────────────────

/**
 * Einen leeren Baustein anlegen: Prozess + Diagramm + Ebene.
 *
 * Die Elemente füllt der Aufrufer — beim Herauslösen aus einem Sub-Prozess
 * werden sie verschoben, beim Neuanlegen bleibt er leer.
 *
 * @returns {{process: object, diagram: object, plane: object}}
 */
export function createBaustein(definitions, bpmnFactory, name) {
  const process = bpmnFactory.create('bpmn:Process', {
    id: freieId(definitions, 'Baustein'),
    name: name || 'Neuer Baustein',
    isExecutable: false,
  });
  process.$parent = definitions;
  // Über beide Wege setzen: moddle kennt die Eigenschaft aus der Erweiterung,
  // $attrs trägt sie auch dann, wenn die Erweiterung einmal fehlt.
  process.set(MARKER, true);
  if (!process.$attrs) process.$attrs = {};
  process.$attrs[MARKER] = true;

  const plane = bpmnFactory.create('bpmndi:BPMNPlane', {
    id: freieId(definitions, 'BausteinPlane'),
    bpmnElement: process,
  });
  const diagram = bpmnFactory.create('bpmndi:BPMNDiagram', {
    id: freieId(definitions, 'BausteinDiagram'),
    plane,
  });
  plane.$parent = diagram;
  diagram.$parent = definitions;

  definitions.rootElements = [...(definitions.rootElements || []), process];
  definitions.diagrams = [...(definitions.diagrams || []), diagram];

  return { process, diagram, plane };
}

/**
 * Einen Baustein entfernen — nur, wenn ihn niemand mehr aufruft.
 * @returns {{ok: boolean, aufrufe: number}}
 */
export function removeBaustein(definitions, processId) {
  const aufrufe = findCallSites(definitions, processId);
  if (aufrufe.length > 0) return { ok: false, aufrufe: aufrufe.length };

  definitions.rootElements = (definitions.rootElements || []).filter((e) => e.id !== processId);
  definitions.diagrams = (definitions.diagrams || [])
    .filter((d) => d.plane?.bpmnElement?.id !== processId);
  return { ok: true, aufrufe: 0 };
}
